// What the screen and the API share about cloud sandboxes and previews
// (`@trameria/cloud-api/sandboxes-shared`). Types only, plus the names of the
// events and of the failures a sandbox can show.
//
// REST (all with a session; device tokens and MCP are refused with 403):
// - GET    /api/me/cloud                       → MeCloudResponse
// - POST   /api/me/cloud/:profile              → 201 SandboxDTO (makes it; allowed and not made yet)
// - POST   /api/me/cloud/:profile/wake         → 202 SandboxDTO
// - POST   /api/me/cloud/:profile/update-clis  → 202 SandboxDTO (running only)
// - DELETE /api/me/cloud/:profile              → 202 SandboxDTO (removes it; the allowance stays)
// - GET    /api/admin/cloud                    → AdminCloudResponse (operators)
// - POST   /api/admin/cloud { email, profile } → 201 AdminSandboxDTO
// - DELETE /api/admin/cloud/:userId/:profile   → 202 (revokes; the row goes once removed)
// - POST   /api/preview/ticket { host, path }  → PreviewTicketResponse (developers and operators)
// - GET    /api/preview/list                   → PreviewListResponse
// - POST   /api/preview/stop { sandbox, name } → 202 (the owner and operators)
// Events (the person's event stream): `cloud.status { profile }` to the
// owner when anything on SandboxDTO changes; `preview.list {}` to every
// developer and operator when the list of previews changes.

export const SANDBOX_PROFILES = ["general", "dev"] as const;
export type SandboxProfile = (typeof SANDBOX_PROFILES)[number];

/** What is wanted: written by the person, the operator, wake triggers and the sleep decision. */
export type SandboxWant = "awake" | "asleep" | "gone";

/** What is there now (the reconciler moves it toward `want`). */
export type SandboxStatus =
  | "absent"
  | "starting"
  | "running"
  | "stopping"
  | "stopped"
  | "queued"
  | "deleting"
  | "error";

export const SANDBOX_EVENT_STATUS = "cloud.status";
export const SANDBOX_EVENT_PREVIEWS = "preview.list";

/**
 * A failure, as a code and values; the screen words it in the reader's
 * language. Codes:
 * - k8s_http { op, target, status, reason, message } / k8s_timeout { op, target, seconds } /
 *   k8s_network { op, target, message }: a call to Kubernetes failed
 * - collision { target }: an object of another person or another instance has the name
 * - no_dev_image: no development image is configured
 * - profile_mismatch: the StatefulSet is of the other profile
 * - start_timeout { minutes, reason }: no ready pod in time
 * - stop_timeout { minutes, pod }: the pod did not go away in time (it stopped anyway)
 * - delete_timeout { minutes, left }: objects are still there (removal goes on)
 * - unreachable { minutes }: the agent in the sandbox did not connect in time
 */
export interface SandboxErrorDTO {
  code: string;
  params: Record<string, string | number | boolean | null | string[]>;
}

/** The CLIs in the sandbox, as its helper last reported them. */
export interface SandboxClisDTO {
  claude: string | null;
  codex: string | null;
  /** ISO 8601. */
  checkedAt: string | null;
  updating: boolean;
  lastError: string | null;
  /** The screen asked for an update and the helper has not been told yet. */
  updateWanted: boolean;
  /**
   * The screen's last ask for an update (ISO 8601): when it was asked and,
   * once the helper was told (null while `updateWanted`), when and what its
   * report said then (its last check and versions). It is done once
   * `checkedAt` differs from `handed.checkedAt` and nothing updates; its
   * result is the versions now against `handed`, or `lastError`.
   */
  lastUpdate: {
    askedAt: string;
    handed: {
      at: string;
      checkedAt: string | null;
      claude: string | null;
      codex: string | null;
    } | null;
  } | null;
}

export interface SandboxDTO {
  profile: SandboxProfile;
  /** An operator allowed this profile for the person. */
  allowed: boolean;
  /** The person made it (it has a device) and has not removed it. */
  created: boolean;
  status: SandboxStatus;
  want: SandboxWant;
  /** The agent device of the sandbox (the device list shows it as trameria's). */
  deviceId: string | null;
  lastError: SandboxErrorDTO | null;
  /** 1-based place in the queue for memory while `queued`; else null. */
  queuePosition: number | null;
  clis: SandboxClisDTO | null;
  /**
   * What the agent inside said of the list of trusted devices: its instruct
   * key (what the person's app adds), the list it holds (null: none yet)
   * and how many members it takes instructions from. null: it has not said.
   */
  trust: { key: string | null; list: string | null; takes: number } | null;
}

export interface MeCloudResponse {
  general: SandboxDTO;
  dev: SandboxDTO;
}

export interface AdminSandboxDTO {
  userId: string;
  email: string | null;
  name: string | null;
  profile: SandboxProfile;
  /** The Kubernetes name (StatefulSet). */
  sandbox: string;
  status: SandboxStatus;
  want: SandboxWant;
  deviceId: string | null;
  image: string | null;
  lastError: SandboxErrorDTO | null;
  allowedAt: string;
  allowedBy: string | null;
  /** Set while a revoked sandbox is being removed. */
  revokedAt: string | null;
  lastActiveAt: string | null;
  statusChangedAt: string;
}

export interface AdminCloudResponse {
  items: AdminSandboxDTO[];
  /** GiB in use (starting, running, stopping) and the budget. */
  memory: { usedGi: number; budgetGi: number };
}

export interface AllowSandboxBody {
  email: string;
  profile: SandboxProfile;
}

export interface PreviewTicketBody {
  /** The preview's host (`<name>--<key>.<domain>`). */
  host: string;
  /** Where to land on it (a path on the same host). */
  path: string;
}

export interface PreviewTicketResponse {
  /** Open this: the relay takes the one-minute ticket and sets its cookie. */
  url: string;
}

export type PreviewRowStatus = "running" | "missing" | "failed" | "conflict";

export interface PreviewRowDTO {
  name: string;
  url: string;
  status: PreviewRowStatus;
}

export interface PreviewSandboxDTO {
  /** The sandbox's Kubernetes name (what `stop` names). */
  sandbox: string;
  owner: { userId: string; name: string | null };
  status: SandboxStatus;
  previewSeenAt: string | null;
  canStop: boolean;
  /** Empty unless the sandbox is running. */
  previews: PreviewRowDTO[];
}

export interface PreviewListResponse {
  sandboxes: PreviewSandboxDTO[];
}

export interface PreviewStopBody {
  sandbox: string;
  name: string;
}

/**
 * PUT /api/sandbox/report (the sandbox's helper, with the sandbox device's
 * token). `previews` and `problems` only from a development sandbox.
 */
export interface SandboxReportBody {
  clis: {
    claude: string | null;
    codex: string | null;
    checkedAt: string | null;
    updating: boolean;
    lastError: string | null;
  };
  previews?: { name: string; port: number }[];
  problems?: { name: string; reason: "missing" | "failed" | "conflict" }[];
}

export interface SandboxReportReply {
  /** Previews stopped from the screen since the last reply. */
  stop: string[];
  /** The screen asked for a CLI update (told once). */
  updateClis: boolean;
}
